/**
 * TipTap to AeroDocument Conversion
 * 
 * Converts TipTap editor JSON back to AeroDocument structure.
 * This enables editable right panel with auto-save.
 */

import { Editor } from '@tiptap/react';
import type { AeroDocument, DocumentSection, ContentBlock, SectionType, TableColumn, TableRow, TableCell } from '../types/document-schema';
import type { TipTapNode } from './document-to-tiptap';
import { logger } from './logger';

/**
 * Convert TipTap editor content to AeroDocument
 * 
 * Strategy:
 * 1. Walk TipTap JSON tree
 * 2. Identify sections by heading nodes with id attributes
 * 3. Group content blocks under their section
 * 4. Reconstruct document hierarchy
 * 5. Preserve metadata from base document
 */
export function convertTipTapToDocument(
  editor: Editor,
  baseDocument: AeroDocument
): AeroDocument {
  const tipTapJson = editor.getJSON();
  
  // Parse sections and blocks from TipTap content
  const sections = parseSectionsFromTipTap(tipTapJson.content || [], baseDocument);
  
  // Create updated document
  // CRITICAL: Keep the existing version - server will generate new version after validation
  // If we generate a new version here, it won't match the cache and will cause 409
  const updatedDocument: AeroDocument = {
    ...baseDocument,
    body: sections,
    lastModified: new Date().toISOString(),
    modifiedBy: 'user', // TODO: Get from auth context
    version: baseDocument.version, // Keep existing version for cache comparison
  };
  
  return updatedDocument;
}

/**
 * Parse sections from TipTap content nodes
 */
function parseSectionsFromTipTap(
  nodes: TipTapNode[],
  baseDocument: AeroDocument
): DocumentSection[] {
  const sections: DocumentSection[] = [];
  let currentSection: DocumentSection | null = null;
  let currentBlocks: ContentBlock[] = [];
  
  for (const node of nodes) {
    // Check if this is a heading with an ID (section marker)
    if (node.type === 'heading' && node.attrs?.id) {
      // Save previous section if exists
      if (currentSection) {
        currentSection.blocks = currentBlocks;
        sections.push(currentSection);
        currentBlocks = [];
      }
      
      // Find the original section from base document to preserve metadata
      const originalSection = findSectionById(baseDocument.body, node.attrs.id);
      
      // Extract section number and title from heading text
      const headingText = extractTextFromNode(node);
      const { number, title } = parseSectionHeading(headingText);
      
      // Determine if user intentionally removed the section number
      // If original had a number but parsed heading doesn't start with it, user removed it
      let finalNumber = number;
      if (!number && originalSection?.number) {
        // Check if the original number is missing from the heading text
        const originalNumber = originalSection.number;
        const headingStartsWithOriginalNumber = headingText.startsWith(originalNumber + ' ');
        
        if (!headingStartsWithOriginalNumber) {
          // User removed the number - keep it empty (don't restore from original)
          logger.debug(`[TipTap] Section ${originalSection.id}: User removed number "${originalNumber}" - respecting removal`);
          finalNumber = '';
        } else {
          // This shouldn't happen (regex should have matched), but fall back to original
          finalNumber = originalNumber;
        }
      }
      
      // Create new section (preserve original metadata)
      currentSection = originalSection ? {
        ...originalSection,
        number: finalNumber,
        title: title || originalSection.title,
        lastModified: new Date().toISOString(),
        modifiedBy: 'user',
      } : {
        id: node.attrs.id,
        type: getSectionTypeFromHeadingLevel(node.attrs.level),
        number: number || '',
        title: title || 'Untitled Section',
        parent: undefined,
        children: [],
        blocks: [],
        required: false,
      };
    } else {
      // This is a content block
      const block = convertTipTapNodeToBlock(node);
      if (block) {
        currentBlocks.push(block);
      }
    }
  }
  
  // Save last section
  if (currentSection) {
    currentSection.blocks = currentBlocks;
    sections.push(currentSection);
  }
  
  // Rebuild section hierarchy (parent-child relationships)
  rebuildSectionHierarchy(sections);
  
  return sections;
}

/**
 * Find section by ID in document body
 */
function findSectionById(sections: DocumentSection[], id: string): DocumentSection | null {
  for (const section of sections) {
    if (section.id === id) {
      return section;
    }
    // Recursively search children
    const childSections = sections.filter(s => section.children.includes(s.id));
    for (const child of childSections) {
      const found = findSectionById([child], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Extract plain text from a TipTap node
 */
function extractTextFromNode(node: TipTapNode): string {
  if (node.type === 'text') {
    return node.text || '';
  }
  
  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }
  
  return '';
}

/**
 * Extracted list item structure
 */
interface ExtractedListItem {
  id: string;
  text: string;
  children?: ExtractedListItem[];
}

/**
 * Extract list item with proper handling for nested lists
 * 
 * TipTap list structure:
 * - listItem contains: [paragraph (text), bulletList/orderedList (nested)]
 * 
 * We extract text from the paragraph and recursively process nested lists as children
 */
function extractListItem(listItemNode: TipTapNode): ExtractedListItem {
  const itemId = `li-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  let text = '';
  let children: ExtractedListItem[] = [];
  
  if (listItemNode.content && Array.isArray(listItemNode.content)) {
    for (const child of listItemNode.content) {
      if (child.type === 'paragraph') {
        // Extract text from paragraph content
        text = extractTextFromNode(child);
      } else if (child.type === 'bulletList' || child.type === 'orderedList') {
        // Recursively extract nested list items as children
        if (child.content && Array.isArray(child.content)) {
          children = child.content.map((nestedItem: TipTapNode) => extractListItem(nestedItem));
        }
      }
    }
  }
  
  const result: ExtractedListItem = {
    id: itemId,
    text: text
  };
  
  if (children.length > 0) {
    result.children = children;
  }
  
  return result;
}

/**
 * Parse section number and title from heading text
 * Format: "1.2 Section Title" → { number: "1.2", title: "Section Title" }
 * Also handles letter-based numbers: "ES Executive Summary" → { number: "ES", title: "Executive Summary" }
 */
function parseSectionHeading(text: string): { number: string; title: string } {
  // Try numeric section numbers first: "1.2.3 Title" or "1 Title"
  const numericMatch = text.match(/^([\d.]+)\s+(.+)$/);
  if (numericMatch) {
    return {
      number: numericMatch[1],
      title: numericMatch[2]
    };
  }
  
  // Try letter-based section numbers: "ES Title", "A Title", "A.1 Title"
  // Match uppercase letters optionally followed by dots and numbers
  const letterMatch = text.match(/^([A-Z]+(?:[\d.]*)?)\s+(.+)$/);
  if (letterMatch) {
    return {
      number: letterMatch[1],
      title: letterMatch[2]
    };
  }
  
  // No number found, entire text is title
  return {
    number: '',
    title: text
  };
}

/**
 * Get section type from heading level
 */
function getSectionTypeFromHeadingLevel(level: number): SectionType {
  switch (level) {
    case 1:
      return 'section';
    case 2:
      return 'subsection';
    case 3:
      return 'subsubsection';
    default:
      return 'section';
  }
}

/**
 * Convert TipTap node to ContentBlock
 */
function convertTipTapNodeToBlock(node: TipTapNode): ContentBlock | null {
  // CRITICAL: Preserve existing block ID from data-block-id attribute
  // Generate new ID only if missing (for new user-created blocks)
  const blockId = node.attrs?.['data-block-id'] || `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  switch (node.type) {
    case 'paragraph': {
      const paragraphText = extractTextFromNode(node);
      if (!paragraphText.trim()) {
        return null; // Skip empty paragraphs
      }
      return {
        id: blockId,
        type: 'paragraph',
        content: {
          text: paragraphText
        }
      };
    }
    
    case 'blockquote': {
      // Blockquote wraps paragraphs - extract text from nested content
      const quoteText = extractTextFromNode(node);
      if (!quoteText.trim()) {
        return null;
      }
      return {
        id: blockId,
        type: 'paragraph',
        content: {
          text: quoteText,
          style: 'quote'
        }
      };
    }
    
    case 'bulletList':
      return {
        id: blockId,
        type: 'bulleted_list',
        content: {
          items: node.content?.map((listItem: TipTapNode) => extractListItem(listItem)) || []
        }
      };
    
    case 'orderedList':
      return {
        id: blockId,
        type: 'numbered_list',
        content: {
          items: node.content?.map((listItem: TipTapNode) => extractListItem(listItem)) || []
        }
      };
    
    case 'table': {
      // Extract table structure with preserved row/column IDs
      const rows = node.content || [];
      const headerRow = rows[0];
      const dataRows = rows.slice(1);
      
      // Build columns from header, preserving data-col-id attributes
      const columns: TableColumn[] = headerRow?.content?.map((cell: TipTapNode, index: number) => ({
        id: cell.attrs?.['data-col-id'] as string || `col-${index}`,
        label: extractTextFromNode(cell),
        type: 'text' as const
      })) || [];
      
      // Build data rows, preserving data-row-id and data-col-id attributes
      const tableRows: TableRow[] = dataRows.map((row: TipTapNode, rowIndex: number) => {
        const rowId = row.attrs?.['data-row-id'] as string || `row-${rowIndex}`;
        const cells: Record<string, TableCell> = {};
        
        row.content?.forEach((cell: TipTapNode, cellIndex: number) => {
          // Use column ID from cell attribute or from columns array
          const colId = cell.attrs?.['data-col-id'] || (columns[cellIndex]?.id) || `col-${cellIndex}`;
          cells[colId] = {
            value: extractTextFromNode(cell)
          };
        });
        
        return { 
          id: rowId,
          cells 
        };
      });
      
      return {
        id: blockId,
        type: 'table',
        content: {
          columns,
          rows: tableRows
        }
      };
    }
    
    case 'image':
      return {
        id: blockId,
        type: 'image',
        content: {
          url: node.attrs?.src || '',
          alt: node.attrs?.alt || '',
          caption: node.attrs?.title || ''
        }
      };
    
    case 'codeBlock':
      return {
        id: blockId,
        type: 'code_block',
        content: {
          code: extractTextFromNode(node),
          language: node.attrs?.language || 'text'
        }
      };
    
    case 'vectorGraphic':
      return {
        id: blockId,
        type: 'vector_graphic',
        content: {
          format: node.attrs?.format || 'mermaid',
          content: node.attrs?.content || '',
          caption: node.attrs?.caption || undefined,
          figureNumber: node.attrs?.figureNumber || undefined,
          diagramId: node.attrs?.diagramId || undefined,
          diagramVersion: node.attrs?.diagramVersion || undefined,
          diagramTitle: node.attrs?.diagramTitle || undefined,
        }
      };
    
    case 'planTask':
      return {
        id: blockId,
        type: 'plan_task',
        content: {
          description: node.attrs?.description || '',
          status: node.attrs?.status || 'pending',
          order: node.attrs?.order || 0,
          targetDocumentName: node.attrs?.targetDocumentName || undefined,
          targetDocumentId: node.attrs?.targetDocumentId || undefined,
          result: node.attrs?.result || undefined,
          error: node.attrs?.error || undefined,
        }
      };
    
    case 'horizontalRule':
      return {
        id: blockId,
        type: 'divider',
        content: {}
      };
    
    case 'heading':
      // Headings without IDs are treated as content blocks (not section headers)
      if (!node.attrs?.id) {
        return {
          id: blockId,
          type: 'heading',
          content: {
            text: extractTextFromNode(node),
            level: node.attrs?.level || 2
          }
        };
      }
      // Headings with IDs are section headers, handled separately
      return null;
    
    default:
      logger.warn('Unknown TipTap node type:', node.type);
      return null;
  }
}

/**
 * Rebuild section hierarchy (parent-child relationships)
 * Based on heading levels and document order
 */
function rebuildSectionHierarchy(sections: DocumentSection[]): void {
  // Clear existing parent/child relationships
  sections.forEach(s => {
    s.parent = undefined;
    s.children = [];
  });
  
  // Build hierarchy based on section types
  const stack: DocumentSection[] = [];
  
  for (const section of sections) {
    // Find parent by popping stack until we find appropriate level
    while (stack.length > 0) {
      const potential = stack[stack.length - 1];
      if (isChildOf(section, potential)) {
        // Found parent
        section.parent = potential.id;
        potential.children.push(section.id);
        break;
      } else {
        // Not a child of this section, pop and try next
        stack.pop();
      }
    }
    
    // Add to stack for potential children
    stack.push(section);
  }
}

/**
 * Determine if section A is a child of section B
 */
function isChildOf(child: DocumentSection, potentialParent: DocumentSection): boolean {
  const childLevel = getSectionLevel(child.type);
  const parentLevel = getSectionLevel(potentialParent.type);
  
  // Child must have higher level number (deeper nesting)
  return childLevel > parentLevel;
}

/**
 * Get numeric level for section type
 */
function getSectionLevel(type: SectionType): number {
  switch (type) {
    case 'section':
      return 1;
    case 'subsection':
      return 2;
    case 'subsubsection':
      return 3;
    case 'appendix':
      return 1;
    default:
      return 1;
  }
}
